import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Shield, Settings as SettingsIcon, Save, RefreshCw, Upload, X } from 'lucide-react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Card, Button, Breadcrumb, PageHeader, DataTable, Badge, Input, Select, FormRow, Textarea,
} from '../components/common';
import api from '../services/api';
import useLang from '../i18n';

const ACTION_COLORS = {
  CREATE: 'green', UPDATE: 'blue', DELETE: 'red',
  LOGIN: 'gray', LOGOUT: 'gray', VIEW: 'gray',
};

export function AuditTrail() {
  const { t } = useLang();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery(
    ['audit', page],
    () => api.get('/audit', { params: { page, limit: 20 } }),
    { keepPreviousData: true }
  );
  const logs = data?.data || [];

  const COLUMNS = [
    { key: 'createdAt', label: t('date'), render: (v) => (
        <span className="text-gray-400 text-xs">{v ? new Date(v).toLocaleString('fa-IR') : '—'}</span>
      )
    },
    { key: 'userId', label: t('user'), render: (v, row) => (
        <span className="font-medium text-gray-800">{row.userId?.name || v || '—'}</span>
      )
    },
    { key: 'action', label: t('actions'), render: (v) => (
        <Badge label={v} color={ACTION_COLORS[v] || 'gray'} />
      )
    },
    { key: 'resource',   label: t('resource'),   render: (v) => <span className="text-gray-600 text-xs font-mono">{v}</span> },
    { key: 'ipAddress',  label: t('ipAddress'),  render: (v) => <span className="text-gray-400 text-xs font-mono">{v || '—'}</span> },
    { key: 'resourceId', label: t('id'),  render: (v) => v ? <code className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded text-gray-500">{String(v).slice(-8)}</code> : '—' },
  ];

  return (
    <div>
      <Breadcrumb items={[t('company'), t('audit')]} />
      <PageHeader
        title={t('audit')}
        subtitle={t('auditSubtitle')}
      />

      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: t('create'),      count: logs.filter(l => l.action === 'CREATE').length, color: 'text-green-600', bg: 'bg-green-50' },
          { label: t('update'),    count: logs.filter(l => l.action === 'UPDATE').length, color: 'text-blue-600',  bg: 'bg-blue-50' },
          { label: t('delete'),       count: logs.filter(l => l.action === 'DELETE').length, color: 'text-red-600',   bg: 'bg-red-50' },
          { label: t('loginLogout'), count: logs.filter(l => ['LOGIN','LOGOUT'].includes(l.action)).length, color: 'text-gray-600', bg: 'bg-gray-100' },
        ].map(s => (
          <Card key={s.label} className={`p-3 text-center ${s.bg}`}>
            <p className={`text-2xl font-bold ${s.color}`}>{s.count}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </Card>
        ))}
      </div>

      <Card>
        <DataTable columns={COLUMNS} data={logs} loading={isLoading} emptyTitle={t('noData')}
          pagination={data?.pagination} onPageChange={setPage} />
      </Card>
    </div>
  );
}

export function Settings() {
  const { t } = useLang();
  const qc = useQueryClient();
  const [logoPreview, setLogoPreview] = useState('');
  const [uploading, setUploading] = useState(false);

  const { data: companyData, isLoading, refetch } = useQuery(
    'company-settings',
    () => api.get('/companies/profile'),
    { staleTime: 0 }
  );
  const company = companyData?.data || {};

  const { register, handleSubmit, reset, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      name: '',
      nameEn: '',
      registrationNo: '',
      taxId: '',
      phone: '',
      email: '',
      address: '',
      website: '',
      logo: '',
      baseCurrency: 'AFN',
      secondCurrency: 'USD',
      settings: {
        autoJournal: true,
        requireApproval: false,
        invoicePrefix: 'INV',
        journalPrefix: 'JV',
        dateFormat: 'jalali',
      }
    }
  });

  useEffect(() => {
    if (company && Object.keys(company).length > 0) {
      reset({
        name: company.name || '',
        nameEn: company.nameEn || '',
        registrationNo: company.registrationNo || '',
        taxId: company.taxId || '',
        phone: company.phone || '',
        email: company.email || '',
        address: company.address || '',
        website: company.website || '',
        logo: company.logo || '',
        baseCurrency: company.baseCurrency || 'AFN',
        secondCurrency: company.secondCurrency || 'USD',
        settings: {
          autoJournal: company.settings?.autoJournal !== false,
          requireApproval: company.settings?.requireApproval || false,
          invoicePrefix: company.settings?.invoicePrefix || 'INV',
          journalPrefix: company.settings?.journalPrefix || 'JV',
          dateFormat: company.settings?.dateFormat || 'jalali',
        }
      });
      setLogoPreview(company.logo || '');
    }
  }, [company, reset]);

  const uploadLogo = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    
    setUploading(true);
    try {
      const res = await api.post('/companies/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (res.data?.logo) {
        setLogoPreview(res.data.logo);
        setValue('logo', res.data.logo);
        toast.success(t('uploadSuccess'));
      }
    } catch (error) {
      toast.error(t('uploadError'));
    } finally {
      setUploading(false);
    }
  };

  const updateMutation = useMutation(
    (data) => api.put('/companies/profile', data),
    {
      onSuccess: () => {
        qc.invalidateQueries('company-settings');
        refetch();
        toast.success(t('saveChanges'));
      },
      onError: () => toast.error(t('saveError'))
    }
  );

  const onSubmit = (data) => {
    updateMutation.mutate(data);
  };

  if (isLoading) {
    return <div className="flex justify-center py-20">{t('loading')}</div>;
  }

  return (
    <div>
      <Breadcrumb items={[t('company'), t('settings')]} />
      <PageHeader title={t('settings')} />

      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          
          <Card className="p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-4 flex items-center gap-2">
              <SettingsIcon className="w-4 h-4 text-gray-400" /> {t('companyInfo')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1.5">{t('logo')}</label>
                <div className="flex items-center gap-4">
                  {logoPreview ? (
                    <div className="relative">
                      <img src={logoPreview} alt="Logo" className="h-16 w-auto object-contain border rounded-lg p-1" />
                      <button
                        type="button"
                        onClick={() => { setLogoPreview(''); setValue('logo', ''); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                      <Upload className="w-6 h-6" />
                    </div>
                  )}
                  <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-xl text-sm font-semibold">
                    {t('uploadLogo')}
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => uploadLogo(e.target.files[0])}
                      disabled={uploading}
                    />
                  </label>
                  {uploading && <span className="text-sm text-gray-500">{t('uploading')}</span>}
                </div>
                <p className="text-xs text-gray-400 mt-1">{t('logoFormat')}</p>
                <input type="hidden" {...register('logo')} />
              </div>

              <FormRow>
                <Input 
                  label={t('companyName')} 
                  placeholder={t('phCompanyName')}
                  {...register('name', { required: t('required') })}
                  error={errors.name?.message}
                />
                <Input 
                  label={t('companyNameEn')} 
                  placeholder={t('phCompanyNameEn')}
                  {...register('nameEn')}
                />
              </FormRow>

              <FormRow>
                <Input label={t('registrationNo')} placeholder={t('phRegistrationNo')} {...register('registrationNo')} />
                <Input label={t('taxId')} placeholder={t('phTaxId')} {...register('taxId')} />
              </FormRow>

              <FormRow>
                <Input label={t('phone')} placeholder={t('phPhone')} {...register('phone')} />
                <Input label={t('email')} type="email" placeholder={t('phEmail')} {...register('email')} />
              </FormRow>

              <Textarea label={t('address')} rows={2} placeholder={t('phAddress')} {...register('address')} />
              
              <Input label={t('website')} placeholder={t('phWebsite')} {...register('website')} />
            </div>
          </Card>

          <div className="space-y-5">
            <Card className="p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-4">{t('accountingSettings')}</h3>
              <div className="space-y-4">
                <FormRow>
                  <Select 
                    label={t('baseCurrency')} 
                    options={[
                      { value: 'AFN', label: t('afn') },
                      { value: 'USD', label: `${t('usd')} (USD)` },
                      { value: 'EUR', label: 'یورو (EUR)' }
                    ]} 
                    {...register('baseCurrency')}
                  />
                  <Select 
                    label={t('secondCurrency')} 
                    options={[
                      { value: 'USD', label: `${t('usd')} (USD)` },
                      { value: 'EUR', label: 'یورو (EUR)' },
                      { value: 'AFN', label: t('afn') }
                    ]} 
                    {...register('secondCurrency')}
                  />
                </FormRow>

                <FormRow>
                  <Input 
                    label={t('invoicePrefix')} 
                    placeholder="INV"
                    {...register('settings.invoicePrefix')}
                  />
                  <Input 
                    label={t('journalPrefix')} 
                    placeholder="JV"
                    {...register('settings.journalPrefix')}
                  />
                </FormRow>

                <FormRow>
                  <Select 
                    label={t('autoJournal')} 
                    options={[
                      { value: true, label: t('active') },
                      { value: false, label: t('inactive') }
                    ]} 
                    {...register('settings.autoJournal')}
                  />
                  <Select 
                    label={t('requireApproval')} 
                    options={[
                      { value: false, label: t('no') },
                      { value: true, label: t('yes') }
                    ]} 
                    {...register('settings.requireApproval')}
                  />
                </FormRow>

                <Select 
                  label={t('dateFormat')} 
                  options={[
                    { value: 'jalali', label: t('jalali') },
                    { value: 'gregorian', label: t('gregorian') }
                  ]} 
                  {...register('settings.dateFormat')}
                />
              </div>
            </Card>

            <Card className="p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-4">{t('todayRate')}</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700">$</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-xs font-bold text-gray-700">AFN </span>
                  </div>
                  <input 
                    type="number" 
                    className="w-32 px-3 py-1 text-sm border rounded-lg text-left"
                    placeholder={t('ratePlaceholder')}
                    defaultValue="580000"
                  />
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-gray-700">€</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-xs font-bold text-gray-700">AFN </span>
                  </div>
                  <input 
                    type="number" 
                    className="w-32 px-3 py-1 text-sm border rounded-lg text-left"
                    placeholder={t('ratePlaceholder')}
                    defaultValue="630000"
                  />
                </div>
              </div>
              <Button size="sm" className="mt-3 w-full justify-center" icon={<RefreshCw className="w-3.5 h-3.5" />}>
                {t('updateRate')}
              </Button>
            </Card>

            <Button 
              variant="primary" 
              type="submit" 
              className="w-full justify-center" 
              icon={<Save className="w-4 h-4" />}
              loading={updateMutation.isLoading}
            >
              {t('saveChanges')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}